import { MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../services/api"
import { FieldRow, FormPage } from "../../../components/FormPage"
import { AppRegistrationPost } from "../../../models/AppRegistration"
import { ControllerRendererProps } from "../../../types/reactHookForm"

const validationSchema = z.object({
  name: z.string().min(1, "Name is required"),
  clientId: z.string().min(1, "Client ID is required"),
})

const returnURL = `/app-registrations`

export function AppRegistrationCreate() {
  const navigate = useNavigate()

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<AppRegistrationPost>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      clientId: "",
    },
  })

  const onSubmit = (values: AppRegistrationPost) => digitalManagerApi
      .post(`/appRegistrations`, {
        clientId: values.clientId,
        name: values.name,
      })
      .then((res) => {
        if (res.status === StatusCodes.CREATED) {
          toast.success("App registration created")
          navigate(returnURL)
        }
      })
      .catch(() => {
        toast.error("Cannot create app registration")
      })

  const handleNameChange = useCallback(
    (field: ControllerRenderProps<AppRegistrationPost, "name">) => (value: string) =>
      field.onChange(value),
    []
  )

  const handleClientIdChange = useCallback(
    (field: ControllerRenderProps<AppRegistrationPost, "clientId">) => (value: string) =>
      field.onChange(value),
    []
  )

  const renderNameTextField = useCallback(
    ({ field }: ControllerRendererProps<AppRegistrationPost, "name">) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleNameChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [errors.name, handleNameChange]
  )

  const renderClientIdTextField = useCallback(
    ({ field }: ControllerRendererProps<AppRegistrationPost, "clientId">) => (
      <MUITextField
        id="clientId"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleClientIdChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.clientId?.message === "string"
            ? { clientId: { message: errors.clientId.message } }
            : undefined
        }
      />
    ),
    [errors.clientId, handleClientIdChange]
  )

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Name">
        <Controller name="name" control={control} render={renderNameTextField} />
      </FieldRow>
      <FieldRow label="Client ID">
        <Controller name="clientId" control={control} render={renderClientIdTextField} />
      </FieldRow>
    </FormPage>
  )
}
